import { useAppStore } from "@/store";
import { FC, PropsWithChildren, useCallback, useState } from "react";
import { DropItem } from "react-aria-components";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { usePageDragAndDrop } from "@/components/drag-and-drop";
import { importDroppedItems } from "@/modules/importing/drop-and-drop-markdown";
import { EventContext } from "@/domains/metrics/context";
import { MDSTextColor } from "@/mds";
import EmptyNotesImage from "@/assets/images/empty-notes.svg?react";
import { EmptyText, EmptyTextSubtitle, EmptyTextContainer } from "@/components/styling/empty";

interface NotesPageDragAndDropProps extends PropsWithChildren {
  onDrag: (isDragging: boolean) => void;
  isEmpty: boolean;
}

export const NotesPageDragAndDrop: FC<NotesPageDragAndDropProps> = observer(
  ({ onDrag, isEmpty, children }) => {
    const { store } = useAppStore();
    const [isDragging, setIsDragging] = useState(false);

    const handleDropChange = useCallback(
      (isDragging: boolean) => {
        setIsDragging(isDragging);
        onDrag(isDragging);
      },
      [onDrag]
    );

    const handleDropEnter = useCallback(() => {
      if (store.uploadedFileBatches.aBatchIsUploading) return;
      handleDropChange(true);
    }, [store.uploadedFileBatches.aBatchIsUploading, handleDropChange]);

    const handleDropExit = useCallback(() => {
      handleDropChange(false);
    }, [handleDropChange]);

    const handleDrop = useCallback(
      (e: { items: DropItem[] }) => {
        handleDropChange(false);
        importDroppedItems(e.items, store, EventContext.NotesActions);
      },
      [handleDropChange, store]
    );

    usePageDragAndDrop({
      onDrop: handleDrop,
      onDropEnter: handleDropEnter,
      onDropExit: handleDropExit,
    });

    return (
      <>
        <DropZoneWrapper>
          {children}
          {isDragging && !isEmpty && (
            <DragTargetShade>
              <DragTarget>
                <EmptyNotesImage />
                <EmptyTextContainer>
                  <EmptyText color={MDSTextColor.Emphasis}>Drag and drop notes into Mem</EmptyText>
                  <EmptyTextSubtitle color={MDSTextColor.Emphasis}>
                    Supported file types: Text (.txt), Markdown (.md)
                  </EmptyTextSubtitle>
                </EmptyTextContainer>
              </DragTarget>
            </DragTargetShade>
          )}
        </DropZoneWrapper>
      </>
    );
  }
);

const DropZoneWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  transition: all 0.2s ease;
`;

const DragTargetShade = styled.div`
  background-color: ${({ theme }) => theme.colors.background.diff};
  display: flex;
  margin: ${({ theme }) => theme.spacing["4"]};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  svg {
    color: ${({ theme }) => theme.colors.icon.emphasis};
  }
`;

const DragTarget = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["4"]};
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  background-color: ${({ theme }) =>
    theme.colors.background.banner.emphasis + "0d"}; /* 5% opacity*/
  z-index: 10;
  font-size: 1.5rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
`;
